<template>
	<div class="flex flex-row min-h-screen max-w-full" id="top">
		<sidebar> </sidebar>
		<div class="flex-1 max-w-full min-w-0 bg-gray-100" id="main-content">
			<div class="flex flex-col min-h-full align-middle max-w-full">
				<div class="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-gray-100 px-4 py-2 text-3xl logo-area">
					<div class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
						<!-- <img src="/ERM_EcoDesign-Horizontal-White-Header.svg" class="h-11 object-scale-down object-center md:h-7" />&nbsp; -->
						<div
							v-if="themeStore.getCurrentTheme.templateUI?.menulogoImageBinary && themeStore.getCurrentTheme.templateUI?.menulogoImageContentType"
							v-html="
								createImageFromBinary(
									themeStore.getCurrentTheme.templateUI?.menulogoImageBinary,
									themeStore.getCurrentTheme.templateUI?.menulogoImageContentType
								)
							"
							class="h-full shrink-0 py-1 default-logo"
							style="max-height: 100%"
						></div>
						<img v-else src="/ERM_EcoDesign-Horizontal-White-Header.svg" class="h-full shrink-0 py-1 default-logo" style="max-height: 1.5em" />
						<div
							v-if="showEnvironmentBadge"
							class="environment-badge inline-flex max-w-full items-center justify-center rounded-md px-3 py-1 text-[0.6rem] font-bold uppercase leading-none tracking-[0.08em] sm:px-4 sm:text-[0.7rem]"
							:style="{ backgroundColor: environmentSettings.backgroundColor, color: environmentSettings.textColor }"
						>
							{{ environmentSettings.environmentName }}
						</div>
					</div>

					<div class="flex items-center gap-4">
						<div class="flex flex-row justify-items-center gap-2 text-xl" v-if="apiCount > 0">
							Loading
							<ProgressBar style="display: flex; width: 70px" mode="indeterminate" />
						</div>
						<Button @click="openSupport()" class="text-xs" :disabled="supportLoading">
							<i v-if="supportLoading" class="pi pi-spin pi-spinner mr-2"></i>
							<FontAwesomeIcon v-else :icon="['far', 'life-ring']" size="lg" class="mr-2" />{{ supportLoading ? "Opening…" : "Ask Support" }}
						</Button>
						<Button @click="openFeedback()" class="text-xs"
							><FontAwesomeIcon :icon="['far', 'comment-minus']" size="lg" class="mr-2" />Feedback</Button
						>
						<Button @click="openLCALiteSharepoint()" class="text-xs"
							><FontAwesomeIcon :icon="['far', 'link']" size="lg" class="mr-2" />LCA Lite Sharepoint</Button
						>
						<div class="justify-items-right flex flex-row gap-1 text-xs">
							<SplitButton ref="splitButtonRef" :label="UserName" icon="pi pi-user" :model="itemsM" @click="openUserSplitButtonMenu"> </SplitButton>
						</div>
					</div>
				</div>
				<main role="main">
					<breadcrumb />
					<router-view :key="$route.fullPath"></router-view>
				</main>
			</div>
		</div>
	</div>

	<Drawer
		position="right"
		style="width: 40em; max-width: 40vw"
		v-model:visible="showHelp"
		@hide="closeHelp"
		:showCloseIcon="false"
		:baseZIndex="1000"
	>
		<div class="flex flex-col gap-4 mb-4">
			<div class="flex flex-row items-center justify-between gap-2">
				<h1>{{ userFriendlyName }}</h1>
				<div class="flex flex-row items-center gap-3">
					<button
						v-if="isAdminUser && currentHelpPage"
						aria-label="Edit help content"
						v-tooltip.bottom="'Edit this help content'"
						@click="openHelpEditor"
					>
						<FontAwesomeIcon class="text-primary largeheader" :icon="['far', 'pen-to-square']" />
					</button>
					<button
						v-if="isAdminUser && !currentHelpPage"
						aria-label="Add help content"
						v-tooltip.bottom="'Add help content for this page'"
						@click="openHelpEditor"
					>
						<FontAwesomeIcon class="text-primary largeheader" :icon="['far', 'circle-plus']" />
					</button>
					<button aria-label="Close Help" v-tooltip.bottom="'Close help'" @click="showHelp = false">
						<FontAwesomeIcon class="text-redSignal largeheader" :icon="['far', 'circle-xmark']" />
					</button>
				</div>
			</div>
		</div>
		<div class="help-content prose max-w-none" v-html="helpContent" />
	</Drawer>
	<Dialog
		v-model:visible="showHelpEditor"
		:header="currentHelpPage ? 'Edit help content' : 'Add help content'"
		modal
		class="i2Dialog"
		style="width: 50rem; max-width: 90vw"
	>
		<div class="flex flex-col gap-3">
			<div class="flex flex-col gap-1">
				<label class="font-medium">Title</label>
				<InputText v-model="helpEditorTitle" />
			</div>
			<div class="flex flex-col gap-1">
				<label class="font-medium">Content</label>
				<Editor v-model="helpEditorContent" editorStyle="height:40vh" />
			</div>
		</div>
		<template #footer>
			<Button label="Cancel" class="p-button-text" @click="showHelpEditor = false" />
			<Button label="Save" class="btn p-button" @click="saveHelpEditor" />
		</template>
	</Dialog>
	<Dialog :header="dialogContent.header" class="i2Dialog" position="top" v-model:visible="showDialog" style="min-width: 33vw">
		{{ dialogContent.content }}
	</Dialog>
	<Toast position="top-right" @close="handleToastClose">
		<template #message="slotProps">
			<div class="flex flex-col gap-2 w-full">
				<span class="font-bold">{{ slotProps.message.summary }}</span>
				<span v-html="slotProps.message.detail" style="white-space: pre-line"></span>
			</div>
		</template>
	</Toast>

	<!-- Dedicated toast for new Support / KB messages — bottom-right, gray
	     translucent. Triggered by the "SupportMessageReceived" / "KbMessageReceived"
	     SignalR events; listeners filter by CRUDA permission. -->
	<Toast position="bottom-right" group="newMessage">
		<template #message="slotProps">
			<div class="flex flex-col gap-1 w-full text-white">
				<span class="font-semibold text-sm">{{ slotProps.message.summary }}</span>
				<span class="text-xs opacity-90">{{ slotProps.message.detail }}</span>
				<a
					v-if="(slotProps.message as any).data?.url"
					href="#"
					class="text-xs text-blue-200 underline mt-1 self-start"
					@click.prevent="openNewMessageLink(slotProps.message as any)"
				>
					Open →
				</a>
			</div>
		</template>
	</Toast>
	<DynamicDialog />
	<!--GS start-->
	<Dialog header="Log in" class="i2Dialog" position="top" v-model:visible="showLoginDialog" style="min-width: 33vw"> </Dialog>
	<!--GS end-->

	<ConfirmDialog></ConfirmDialog>
	<ReAuthModal />
	<Dialog
		:header="historyStore.title"
		class="i2Dialog"
		position="top"
		v-model:visible="historyStore.isOpen"
		:style="{ width: '90vw', maxWidth: '1200px' }"
		modal
		:dismissableMask="true"
		@hide="historyStore.close"
	>
		<template #closebutton="{ closeCallback }">
			<span style="cursor: pointer; padding: 0.25rem" @click="closeCallback">
				<FontAwesomeIcon :icon="['far', 'xmark']" />
			</span>
		</template>
		<TemporalHistoryViewer
			v-if="historyStore.isOpen"
			:changeEvents="historyStore.changeEvents"
			:loading="historyStore.loading"
			:hasQueried="historyStore.hasQueried"
			@load="historyStore.reload"
			@clear="historyStore.close"
		/>
	</Dialog>

	<!-- App-level field-level help editors, triggered from any I2Label via useHelpEditor -->
	<I2LabelTooltipEditor
		v-if="tooltipEditorVisible"
		:visible="tooltipEditorVisible"
		@update:visible="tooltipEditorVisible = $event"
		:helpElementName="activeHelpElementName"
		:label="activeLabel"
	/>
	<I2LabelHelpTextEditor
		v-if="helpTextEditorVisible"
		:visible="helpTextEditorVisible"
		@update:visible="helpTextEditorVisible = $event"
		:helpElementName="activeHelpElementName"
		:label="activeLabel"
		:readonly="helpTextReadonly"
	/>
</template>

<script setup lang="ts">
import { nextTick, ref, onMounted, computed } from "vue"
import { useRouter } from "vue-router"
import { useDbOperationNotifier } from "@/composables/useDbOperationNotifier"
import ConfirmDialog from "primevue/confirmdialog"
import ReAuthModal from "./ReAuthModal.vue"
import sidebar from "./sidebar.vue"
import { EventBus } from "../eventBus"
import ProgressBar from "primevue/progressbar"
import Drawer from "primevue/drawer"
import Button from "primevue/button"
import SplitButton from "primevue/splitbutton"
import { ApiClient, HelpPage, ThemeMgmClient, TemplateUI } from "../../entities/NSwagGenerated"
// TODO: Uncomment when TypeScript is updated: UserThemeSelectionClient
import { useThemeManagerStore } from "../store/themeManagerStore"
import { useTemporalHistoryStore } from "../store/temporalHistoryStore"
import TemporalHistoryViewer from "./temporalHistoryViewer.vue"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faCircleXmark, faPenToSquare, faCirclePlus, faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { useEnvironmentSettings } from "../composables/environmentSettings"
import { useSupportContext } from "../composables/useSupportContext"
import { usePrecisionConfiguration } from "../composables/usePrecisionConfiguration"
import * as Session from "../auth/personSessionData"
import breadcrumb from "./breadcrumb.vue"
import I2LabelTooltipEditor from "../crispify/crispy-components/I2LabelTooltipEditor.vue"
import I2LabelHelpTextEditor from "../crispify/crispy-components/I2LabelHelpTextEditor.vue"
import { useHelpEditor } from "../composables/useHelpEditor"
const { tooltipEditorVisible, helpTextEditorVisible, helpTextReadonly, activeHelpElementName, activeLabel } = useHelpEditor()
library.add(faCircleXmark, faPenToSquare, faCirclePlus, faXmark)
import Dialog from "primevue/dialog"
import Editor from "primevue/editor"
import InputText from "primevue/inputtext"
import DialogContent from "../models"
import { authProvider } from "../auth/authProviderFactory"
import Toast, { ToastMessageOptions } from "primevue/toast"
import { useToast } from "primevue/usetoast"
import { collapseEmptyParagraphs } from "@/utils/richTextUtils"

const toast = useToast()
const router = useRouter()

let handleToastClose = () => {}

//let apiCount: number = 0
const apiCount = ref(0)
const showHelp = ref(false)
const helpContent = ref()
const userFriendlyName = ref()
const helpPageName = ref()
const currentHelpPage = ref<HelpPage | null>(null)
const showHelpEditor = ref(false)
const helpEditorTitle = ref("")
const helpEditorContent = ref("")
const isAdminUser = computed(() => Session.SessionInfo.currentUserIsAdmin())
const dialogContent = new DialogContent()
const showDialog = ref(false)
const showLoginDialog = ref(false)
const environmentSettings = useEnvironmentSettings()
const { loadPrecisionSettings } = usePrecisionConfiguration()
const showEnvironmentBadge = computed(() => {
	const environmentName = import.meta.env.VITE_ENVIRONMENT_NAME?.toString() || ""
	return environmentName !== "Production" && environmentName !== "PROD"
})
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const themeMgmClient = new ThemeMgmClient(import.meta.env.VITE_API_URL?.toString())
// TODO: Uncomment when TypeScript is updated
// const userThemeSelectionClient = new UserThemeSelectionClient(import.meta.env.VITE_API_URL?.toString())
const themeStore = useThemeManagerStore()
const historyStore = useTemporalHistoryStore()
const UserName = ref()
const closeHelp = ref()
const availableThemes = ref<TemplateUI[]>([])
const currentThemeId = ref<string | null>(null)

const splitButtonRef = ref<InstanceType<typeof SplitButton> | null>(null)

type MenuItem = {
	label: string
	icon: string
	command?: () => void
	items?: MenuItem[]
}
// Function to create image HTML from binary data
const createImageFromBinary = (binaryData: string, contentType: string): string => {
	if (!binaryData) return ""

	// Check if binaryData is already a data URL
	if (binaryData.startsWith("data:")) {
		return `<img src="${binaryData}" alt="Image" style="max-width: 100%; max-height: 1.5em; height: auto; width: auto; object-fit: contain; background: transparent;" class="transparent-image header-logo-img" />`
	}

	// Handle SVG files specially
	if (contentType === "image/svg+xml" || contentType.includes("svg")) {
		// For SVG, try to decode as text first
		try {
			const svgContent = atob(binaryData)
			// If it's valid SVG content, return it with size constraints
			if (svgContent.includes("<svg")) {
				// Add viewBox and size constraints to SVG
				const modifiedSvg = svgContent.replace(
					/<svg([^>]*)>/,
					'<svg$1 style="max-width: 100%; max-height: 1.5em; height: auto; width: auto; object-fit: contain;" class="header-logo-svg">'
				)
				return modifiedSvg
			}
		} catch (error) {
			// If decoding fails, fall back to data URL
			console.log("SVG decode failed, using data URL:", error)
		}
	}

	// Check if binaryData is already base64 encoded
	let base64Data = binaryData
	if (!binaryData.startsWith("/9j") && !binaryData.startsWith("iVBOR") && !binaryData.startsWith("R0lGOD") && !binaryData.startsWith("PHN2Zw")) {
		// If not base64, assume it's already binary and convert to base64
		try {
			base64Data = btoa(binaryData)
		} catch (error) {
			console.error("Error encoding binary data to base64:", error)
			return ""
		}
	}

	const dataUrl = `data:${contentType};base64,${base64Data}`
	return `<img src="${dataUrl}" alt="Image" style="max-width: 100%; max-height: 1.5em; height: auto; width: auto; object-fit: contain; background: transparent;" class="transparent-image header-logo-img" />`
}
function openFeedback() {
	window.open("https://app.smartsheet.com/b/form/42dc1f1c6a73406382b7962d787f0faf", "_blank")
}

const support = useSupportContext()
const supportLoading = ref(false)
async function openSupport() {
	if (supportLoading.value) return
	supportLoading.value = true
	try {
		await support.openSupportPopup()
	} finally {
		supportLoading.value = false
	}
}

const openUserSplitButtonMenu = async () => {
	await nextTick()
	const el = (splitButtonRef.value as any)?.$el as HTMLElement
	const menuButton = el?.querySelector(".p-splitbutton-menubutton")
	if (menuButton instanceof HTMLElement) {
		menuButton.click()
	}
}

const loadAvailableThemes = async () => {
	try {
		// TODO: Replace with userThemeSelectionClient.getAvailableThemes() when TypeScript is updated
		const themes = await themeMgmClient.getAllThemes()
		availableThemes.value = themes
		updateMenuItems()
	} catch (error) {
		console.error("Error loading themes:", error)
	}
}

const applyTheme = async (themeId: string) => {
	try {
		// Save the user's theme selection to the database (temporary fetch implementation)
		const saveResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/userThemeSelection/saveUserThemeSelection`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${localStorage.getItem("token")}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(themeId),
		})

		if (!saveResponse.ok) {
			throw new Error("Failed to save theme selection")
		}

		// Apply the theme to the interface
		await themeStore.loadSingleCompleteTemplate(themeId, "live")

		// Update current theme and refresh menu
		currentThemeId.value = themeId
		await updateMenuItems()

		// Show success message
		toast.add({
			severity: "success",
			summary: "Theme Applied",
			detail: "Your theme selection has been saved successfully",
			life: 3000,
		})
	} catch (error) {
		console.error("Error applying theme:", error)
		toast.add({
			severity: "error",
			summary: "Theme Error",
			detail: "Failed to apply the selected theme",
			life: 5000,
		})
	}
}

const loadUserTheme = async () => {
	try {
		console.log("🔍 Loading user theme...")
		const token = localStorage.getItem("token")
		console.log("🔑 Token available:", !!token)

		// Temporary implementation using fetch API until TypeScript is updated
		const response = await fetch(`${import.meta.env.VITE_API_URL}/api/userThemeSelection/getCurrentUserTheme`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		})

		console.log("📡 API Response status:", response.status)

		if (response.ok) {
			const userTheme = await response.json()
			console.log("🎨 User theme data:", userTheme)

			if (userTheme && userTheme.selectedTheme && userTheme.selectedTheme.id) {
				console.log("✅ Applying user theme:", userTheme.selectedTheme.templateName, userTheme.selectedTheme.id)

				// Apply the user's theme (this will also save the default theme association if needed)
				await themeStore.loadSingleCompleteTemplate(userTheme.selectedTheme.id, "live")
				currentThemeId.value = userTheme.selectedTheme.id
				await updateMenuItems() // Update menu to show selected theme
				console.log("🎯 User theme applied successfully!")
			} else {
				console.log("⚠️ No valid theme data in response")
			}
		} else {
			const errorText = await response.text()
			console.log("❌ API Error:", response.status, errorText)
		}
	} catch (error) {
		console.error("💥 Error loading user theme:", error)
		// If there's an error, the app will continue with the default theme
	}
}

const itemsM = ref<MenuItem[]>([
	{
		label: "Log out",
		icon: "pi pi-times",
		command: () => {
			logOff()
		},
	},
])

const updateMenuItems = async (forceReload = false) => {
	// Reload themes if requested or if theme list is empty
	if (forceReload || availableThemes.value.length === 0) {
		try {
			const themes = await themeMgmClient.getAllThemes()
			availableThemes.value = themes
		} catch (error) {
			console.error("Error reloading themes:", error)
		}
	}

	// Filter themes to show only active non-standard themes
	const filteredThemes = availableThemes.value.filter((theme) => theme.isActive === true && theme.isStandardTheme === false)

	itemsM.value = [
		{
			label: "Theme",
			icon: "pi pi-palette",
			command: async () => {
				// Reload themes when Theme menu is clicked
				await updateMenuItems(true)
			},
			items: filteredThemes.map((theme) => ({
				label: theme.templateName || "Unnamed Theme",
				icon: theme.id === currentThemeId.value ? "pi pi-check-circle" : "pi pi-circle",
				command: () => {
					if (theme.id) {
						applyTheme(theme.id)
					}
				},
			})),
		},
		{
			label: "Log out",
			icon: "pi pi-times",
			command: () => {
				logOff()
			},
		},
	]
}
const webPath = window.location.protocol + "//" + window.location.host

// Navigates to the deep link carried by a "newMessage" toast and dismisses
// it. Called from the toast's "Open →" link template.
function openNewMessageLink(message: any) {
	const url = message?.data?.url
	if (!url) return
	try {
		toast.remove(message)
	} catch {
		/* ignore */
	}
	router.push(url)
}

onMounted(async () => {
	UserName.value = Session.SessionInfo.currentUser().firstName
	loadPrecisionSettings().catch((error) => console.error("Error loading precision settings:", error))
	await loadAvailableThemes()

	// Per-message toast notifications. Listens to SignalR events emitted by
	// the discussion controllers when a new message is created. Filters by
	// CRUDA permission so only the right team sees them, and skips messages
	// the current user posted themselves.
	const notifier = useDbOperationNotifier()
	const conn = notifier.getConnection()
	if (conn) {
		const me = Session.SessionInfo.currentUser()
		const meId = me?.id ?? ""
		const hasSupportCruda = () => Session.SessionInfo.hasRightsToScreen("Support", Session.PageAccess.Delete)
		const hasKbCruda = () => Session.SessionInfo.hasRightsToScreen("Knowledge Base", Session.PageAccess.Delete)

		conn.on("SupportMessageReceived", (payload: any) => {
			if (!payload || payload.fromPersonId === meId) return
			if (!hasSupportCruda()) return
			toast.add({
				group: "newMessage",
				severity: "secondary",
				summary: `New ${payload.requestType ?? "Support"} message`,
				detail: `${payload.fromName ?? "Someone"} posted on request ${payload.requestNumber ?? ""}`,
				life: 5000,
				styleClass: "newmsg-toast",
				data: { url: `/app/support/${payload.requestId}` },
			} as any)
		})

		conn.on("SupportRequestSubmitted", (payload: any) => {
			if (!payload || payload.fromPersonId === meId) return
			if (!hasSupportCruda()) return
			toast.add({
				group: "newMessage",
				severity: "secondary",
				summary: `New ${payload.requestType ?? "Support"} request`,
				detail: `${payload.fromName ?? "Someone"} submitted request ${payload.requestNumber ?? ""}`,
				life: 5000,
				styleClass: "newmsg-toast",
				data: { url: `/app/support/${payload.requestId}` },
			} as any)
		})

		conn.on("KbMessageReceived", (payload: any) => {
			if (!payload || payload.fromPersonId === meId) return
			if (!hasKbCruda()) return
			toast.add({
				group: "newMessage",
				severity: "secondary",
				summary: payload.messageType === "Reply" ? "New KB reply" : "New KB comment",
				detail: `${payload.fromName ?? "Someone"} on "${payload.articleTitle ?? "(untitled)"}"`,
				life: 5000,
				styleClass: "newmsg-toast",
				data: { url: `/app/knowledge/${payload.articleId}` },
			} as any)
		})
	}

	// Receive postMessage from the support-request popup (supportRequest.vue)
	// so the success toast surfaces in the main app window — the popup closes
	// immediately after submit so a local toast there is never visible.
	window.addEventListener("message", (event: MessageEvent) => {
		if (event.origin !== window.location.origin) return
		const data = event.data as { kind?: string } | null
		if (data?.kind === "supportRequestSubmitted") {
			toast.add({
				severity: "success",
				summary: "Request sent",
				detail: "Your support request has been sent. Thank you!",
				life: 4000,
			})
		}
	})

	// Theme is now loaded by route guard before this component mounts
	// Update currentThemeId based on already loaded theme
	if (themeStore.getCurrentTheme?.templateUI?.id) {
		currentThemeId.value = themeStore.getCurrentTheme.templateUI.id
		await updateMenuItems()
	}
	EventBus.off("IncrementApiCalls")
	EventBus.on("IncrementApiCalls", () => {
		apiCount.value++
	})
	EventBus.off("systemToast")
	EventBus.on("systemToast", (options: any) => {
		if (options.callback) {
			handleToastClose = options.callback
			console.log("handleToastClose", handleToastClose)
		} else {
			handleToastClose = () => {}
		}

		toast.add(options)
	})

	EventBus.off("ZeroApiCalls")
	EventBus.on("ZeroApiCalls", () => {
		apiCount.value = 0
	})

	EventBus.off("ShowDialog")
	EventBus.on("ShowDialog", (dialogContent) => {
		dialogContent = JSON.parse(JSON.stringify(dialogContent))
		showDialog.value = true
	})

	EventBus.off("ShowLoginDialog")
	EventBus.on("ShowLoginDialog", (dialogContent) => {})

	EventBus.off("DecrementApiCalls")
	EventBus.on("DecrementApiCalls", () => {
		if (apiCount.value > 0) {
			apiCount.value--
		}
	})

	EventBus.off("APIError")
	EventBus.on("APIError", (err: any) => {
		if (err.status == 204) {
			helpContent.value = `No help configuration for Help Page Name "${helpPageName}"`
		} else {
			helpContent.value = err.message
		}
	})

	EventBus.off("OpenPageHelp")
	EventBus.on("OpenPageHelp", (helpPageData: any) => {
		userFriendlyName.value = helpPageData.userFriendlyName
		helpPageName.value = helpPageData.helpPageName
		currentHelpPage.value = null
		apiClient
			.getHelpPageByName(helpPageData.helpPageName)
			.then(
				(res) => {
					if (res) {
						currentHelpPage.value = res
						helpContent.value = res.helpContent || "No Help Found"
						userFriendlyName.value = res.userFriendlyName || helpPageData.userFriendlyName
					} else {
						currentHelpPage.value = null
						helpContent.value = `No help configuration for Help Page Name "${helpPageData.helpPageName}"`
					}
				},
				(err) => {
					console.log(err)
					currentHelpPage.value = null
				}
			)
			.finally(() => {
				showHelp.value = true
			})
	})
})

function openHelpEditor() {
	helpEditorTitle.value = currentHelpPage.value?.userFriendlyName ?? userFriendlyName.value ?? ""
	helpEditorContent.value = currentHelpPage.value?.helpContent ?? ""
	showHelpEditor.value = true
}

async function saveHelpEditor() {
	try {
		helpEditorContent.value = collapseEmptyParagraphs(helpEditorContent.value)
		if (currentHelpPage.value && currentHelpPage.value.id) {
			currentHelpPage.value.userFriendlyName = helpEditorTitle.value
			currentHelpPage.value.helpContent = helpEditorContent.value
			await apiClient.helpPut(currentHelpPage.value.id, currentHelpPage.value)
		} else {
			const newPage = new HelpPage()
			newPage.id = self.crypto.randomUUID()
			newPage.helpPageName = helpPageName.value
			newPage.userFriendlyName = helpEditorTitle.value
			newPage.helpContent = helpEditorContent.value
			newPage.isActive = true
			const created = await apiClient.helpPost(newPage)
			currentHelpPage.value = created ?? newPage
		}
		userFriendlyName.value = helpEditorTitle.value
		helpContent.value = helpEditorContent.value || "No Help Found"
		showHelpEditor.value = false
		toast.add({ severity: "success", summary: "Help saved", life: 2000 })
	} catch (err: any) {
		toast.add({ severity: "error", summary: "Failed to save help", detail: err?.message ?? String(err), life: 4000 })
	}
}

function openLCALiteSharepoint() {
	window.open("https://azcollaboration.sharepoint.com/sites/CMU473/SitePages/LCA-Lite.aspx", "_blank")
}

// function logOff() {
// }

const logOff = async () => {
	try {
		await authProvider.signOut()
	} catch (error) {
		console.error("Logout failed", error)
		// Even if logout fails, clear local data and redirect
		authProvider.clearTokens()
		window.location.href = "/login"
	}
}
</script>

<style scoped>
.help-content :deep(p) {
	margin: 0 0 0.75em 0;
	min-height: 1em;
}
.help-content :deep(p:empty)::before,
.help-content :deep(p:has(br:only-child))::before {
	content: "\00a0";
}

.colorWhite {
	color: #ffffff !important;
}
::v-deep(.p-dialog-header) {
	background: var(--p-dialog-title-background, var(--p-ermbrand-green-1)) !important;
	color: var(--p-dialog-title-color, #ffffff) !important;
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}
.logo-area {
	color: var(--p-menu-up-text-color);
	border-bottom: 0.2em solid var(--p-menu-up-border-bottom);
	background-color: var(--p-menu-up-background-color);
}
.logo-area :deep(button:not(.color-picker-btn)) {
	background-color: var(--p-menu-up-button-color, #007bff) !important;
	color: var(--p-menu-up-button-text-color, white) !important;
	border-color: var(--p-menu-up-button-color, #007bff) !important;
}
.default-logo {
	height: 100%;
	display: flex;
	align-items: center;
}
.default-logo :deep(img) {
	height: 100%;
	width: auto;
	object-fit: contain;
}

/* Restore list styles stripped by Tailwind preflight inside help content */
.help-content :deep(ol) {
	list-style-type: decimal;
	padding-left: 1.5em;
}
.help-content :deep(ul) {
	list-style-type: disc;
	padding-left: 1.5em;
}
.help-content :deep(ol ol) {
	list-style-type: lower-alpha;
}
.help-content :deep(ol ol ol) {
	list-style-type: lower-roman;
}
</style>

<style>
.p-toast-message.newmsg-toast {
	background: rgba(55, 65, 81, 0.88) !important;
	color: #ffffff !important;
	border: 1px solid rgba(255, 255, 255, 0.15) !important;
	backdrop-filter: blur(4px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.p-toast-message.newmsg-toast .p-toast-message-content,
.p-toast-message.newmsg-toast .p-toast-summary,
.p-toast-message.newmsg-toast .p-toast-detail {
	color: #ffffff !important;
}
.p-toast-message.newmsg-toast .p-toast-icon-close,
.p-toast-message.newmsg-toast .p-toast-message-icon {
	color: #ffffff !important;
}
.p-toast-message.newmsg-toast .p-toast-icon-close:hover {
	background: rgba(255, 255, 255, 0.15) !important;
}
</style>
